import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { examAPI } from '../services/api';

const Exams = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Forms
  const [examForm, setExamForm] = useState({
    name: '',
    examType: 'Unit Test',
    academicYearId: 1, // Default
    startDate: '',
    endDate: '',
    description: ''
  });

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const res = await examAPI.getExams();
      setExams(res.data.data);
    } catch (error) {
      toast.error('Failed to fetch exams');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (exam = null) => {
    if (exam) {
      setIsEditing(true);
      setEditId(exam.id);
      setExamForm({ ...exam }); // Using spread to copy values
    } else {
      setIsEditing(false);
      setEditId(null);
      setExamForm({
        name: '',
        examType: 'Unit Test',
        academicYearId: 1,
        startDate: '',
        endDate: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await examAPI.updateExam(editId, examForm);
        toast.success('Exam updated');
      } else {
        await examAPI.createExam(examForm);
        toast.success('Exam scheduled');
      }
      setOpenDialog(false);
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this exam?')) {
      try {
        await examAPI.deleteExam(id);
        toast.success('Exam deleted');
        fetchExams();
      } catch (error) {
        toast.error('Failed to delete exam');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Examination Management</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog()}>
          Schedule Exam
        </Button>
      </Box>

      <Grid container spacing={3}>
        {exams.map((exam) => (
          <Grid item xs={12} md={6} lg={4} key={exam.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6">{exam.name}</Typography>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenDialog(exam)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(exam.id)}><DeleteIcon /></IconButton>
                  </Box>
                </Box>
                <Typography color="textSecondary" gutterBottom>{exam.examType}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Date: {new Date(exam.startDate).toLocaleDateString()} - {new Date(exam.endDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {exam.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Exam Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{isEditing ? 'Edit Exam' : 'Schedule New Exam'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="Exam Name"
            fullWidth
            value={examForm.name}
            onChange={(e) => setExamForm({ ...examForm, name: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={examForm.examType}
              onChange={(e) => setExamForm({ ...examForm, examType: e.target.value })}
            >
              <MenuItem value="Unit Test">Unit Test</MenuItem>
              <MenuItem value="Mid Term">Mid Term</MenuItem>
              <MenuItem value="Final">Final</MenuItem>
              <MenuItem value="Quiz">Quiz</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="normal"
            label="Start Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={examForm.startDate}
            onChange={(e) => setExamForm({ ...examForm, startDate: e.target.value })}
          />
          <TextField
            margin="normal"
            label="End Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={examForm.endDate}
            onChange={(e) => setExamForm({ ...examForm, endDate: e.target.value })}
          />
          <TextField
            margin="normal"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={examForm.description}
            onChange={(e) => setExamForm({ ...examForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Exams;
